import { assert } from '@deqo/foundation';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { ObjectManager, ObjectUpdate } from '../../../concepts/objects';
import { Point } from '../../../platform/geom/Point';
import { MouseButtons, MouseInput } from '../../../platform/input/MouseInput';
import { UniversalSurfaceRenderer } from '../../../platform/UniversalSurfaceRenderer';
import { Colors } from '../../../ui';
import { resolve } from '../../Container';
import { AutoLayoutEngine } from '../../packages/autoLayout/AutoLayoutEngine';
import { AutoLayoutPotentialInsert } from '../../packages/autoLayout/LayoutStrategy';
import { Sticky } from '../../packages/paper/Sticky';
import { Camera } from '../Camera';
import { beginMutating } from '../commands/ManipulationCommands';
import { place } from '../commands/OrderCommands';
import { select } from '../commands/SelectionCommands';
import { EditorSession } from '../EditorSession';
import { Physical } from '../entities/Physical';
import { Root } from '../entities/Root';
import { InteractionHelper } from '../InteractionHelper';
import { RootOverride } from '../RootOverride';
import { EffectDirector, Effects } from '../visualization/EffectDirector';
import { QuickCreateInfo } from './QuickCreateInfo';
import { ToolBehavior } from './ToolBehavior';
import { ToolOverlay } from './ToolOverlay';

export class QuickCreateToolBehavior extends ToolBehavior {

    @resolve(AutoLayoutEngine) protected autoLayout!: AutoLayoutEngine;
    @resolve(Camera) private camera!: Camera;
    @resolve(CommandDispatcher) private dispatch!: CommandDispatcher;
    @resolve(EditorSession) private session!: EditorSession;
    @resolve(EffectDirector) private effects!: EffectDirector;
    @resolve(InteractionHelper) private interaction!: InteractionHelper;
    @resolve(ObjectManager) private objects!: ObjectManager;
    @resolve(ToolOverlay) private overlay!: ToolOverlay;

    private subject: Physical | null = null;
    private potentialInsert = null as AutoLayoutPotentialInsert;

    public init() {
        this.burden(
            this.overlay.add(this.paintAutoLayout)
        );
    }

    public onMouseDown(state: string, mi: MouseInput): string | void {

        if (state !== 'idle') return;
        if (mi.button !== MouseButtons.Main) return;
        if (mi.modifiers.any) return;
        if (mi.count !== 2) return;
        if (mi.targetOverlay) return;

        const [hit] = this.interaction.testFor('select', mi.modifiers.alt);
        if (!!hit) return;

        assert(!this.subject, 'QuickCreateToolBehavior: creating with left over subject?');

        this.doPlop(mi.position, false);
        return 'quickCeating';
    }

    public onMouseMove(state: string, mi: MouseInput): string | void {

        if (state !== 'quickCeating') return;
        assert(!!this.subject, 'QuickCreateToolBehavior: creating without subject?');

        this.doPlop(mi.position, false);
    }

    public onMouseUp(state: string, mi: MouseInput): string | void {

        if (state !== 'quickCeating') return;
        assert(!!this.subject, 'QuickCreateToolBehavior: creating without subject?');

        this.doPlop(mi.position, true);
        this.dispatch(select, [[this.subject.id], false]);
        this.dispatch(beginMutating);

        this.subject = null;

        return 'idle';
    }

    private doPlop(vpt: Point, final: boolean) {
        this.objects.update(u => {
            const subject = this.subject = (this.subject ?? this.createSubject(u));
            u.set('key', `edit:${subject.id}`);
            u.set('rolling', !final);
            // Move to relative position
            subject.moveTo(this.resolveRoot().toRelative(this.camera.toModel(vpt)).subtract(subject.size().divide(2)));
            // Support AutoLayout
            this.potentialInsert = this.autoLayout.queryPotentialInsert(this.camera.toModel(vpt), new Set([subject]));
            // If the final move, place
            if (final) {
                // Support AutoLayout
                if (this.potentialInsert) {
                    const { container: target, at } = this.potentialInsert;
                    this.autoLayout.insert(target, at, new Set([subject]));
                    this.potentialInsert = null;
                }
                this.dispatch(place, [subject.id]);
            }
            this.overlay.repaint();
        });
    }

    private createSubject(u: ObjectUpdate) {
        const info = this.session.get(QuickCreateInfo);
        const subject = u.create(info.type ?? Sticky);
        this.resolveRoot().add(subject);
        // Configure
        Object.assign(subject, info.sets ?? {});
        // Animate
        this.effects.trigger(Effects.popIn, subject);
        return subject;
    }

    private resolveRoot() {
        return this.session.get(RootOverride) ?? this.objects.get('root', Root);
    }

    private paintAutoLayout = (gfx: UniversalSurfaceRenderer) => {
        const { container, at, guides, style } = this.potentialInsert ?? {};
        if (!container || !at || !guides) return;

        gfx.setTransform(this.camera.modelViewMatrix);
        gfx.strokeStyle = Colors.primaryLight;
        gfx.lineCap = 'round';
        gfx.lineWidth = style === 'thin' ? 3 : 6;

        for (const g of guides) {
            gfx.beginPath();
            gfx.moveTo(g.p1.x, g.p1.y);
            gfx.lineTo(g.p2.x, g.p2.y);
            gfx.stroke();
        }
    };
}
